import type { CollectorLiveStatus, CollectorSummary } from './types'

/**
 * The filter row's vocabulary: five tabs, not one per live Collector status.
 *
 * `offline` is the tab's own key for the `disconnected` status — the URL says
 * what the tab says. `error` is its own tab and never folds into `degraded`: a
 * collector beating normally while ingesting nothing is the worst state in the
 * feature, and hiding it under Degraded would bury it. `enrolling`/`enrolled`
 * get no tab — an install that hasn't finished is not a health state to filter
 * on — so they show only under `all`, told apart there by their own badge.
 */
export const COLLECTOR_STATUS_FILTERS = [
	'all',
	'connected',
	'degraded',
	'error',
	'offline',
] as const

export type CollectorStatusFilter = (typeof COLLECTOR_STATUS_FILTERS)[number]

/**
 * The one status each non-`all` tab admits. One status per tab, so no
 * collector can ever be counted under two tabs.
 */
const FILTER_STATUS: Record<
	Exclude<CollectorStatusFilter, 'all'>,
	CollectorLiveStatus
> = {
	connected: 'connected',
	degraded: 'degraded',
	error: 'error',
	offline: 'disconnected',
}

/**
 * Tab labels, written here rather than read from the status presets: `Offline`
 * is the tab's word for what the row's badge calls `Disconnected`, so the two
 * vocabularies deliberately differ on that one.
 */
export const COLLECTOR_STATUS_FILTER_LABELS: Record<
	CollectorStatusFilter,
	string
> = {
	all: 'All',
	connected: 'Connected',
	degraded: 'Degraded',
	error: 'Error',
	offline: 'Offline',
}

const DEFAULT_COLLECTOR_STATUS_FILTER: CollectorStatusFilter = 'all'

/** Narrow a raw search-param string to the vocabulary. */
export function isCollectorStatusFilter(
	value: string | undefined,
): value is CollectorStatusFilter {
	return (
		!!value &&
		(COLLECTOR_STATUS_FILTERS as ReadonlyArray<string>).includes(value)
	)
}

/**
 * What the URL means, at read time.
 *
 * The search param carries no parse-time default: an absent `?status=` and an
 * explicit `?status=all` must stay distinguishable in the URL, and the fallback
 * belongs wherever search is turned into meaning (P-22 §1). An unrecognised
 * value degrades to the unfiltered view rather than an empty table — a stale
 * link should show too much, never nothing.
 */
export function resolveCollectorStatusFilter(
	value: string | undefined,
): CollectorStatusFilter {
	return isCollectorStatusFilter(value)
		? value
		: DEFAULT_COLLECTOR_STATUS_FILTER
}

/**
 * How many Collectors each tab would show, for the tabs' count badges.
 *
 * Built from {@link filterCollectorsByStatus} itself, so a tab's count and the
 * rows it lists cannot disagree. Every tab gets an entry even at zero — a filter
 * row whose options come and go is one a user cannot learn.
 */
export function countCollectorsByStatusFilter(
	collectors: ReadonlyArray<CollectorSummary>,
): Record<CollectorStatusFilter, number> {
	return Object.fromEntries(
		COLLECTOR_STATUS_FILTERS.map((filter) => [
			filter,
			filterCollectorsByStatus(collectors, filter).length,
		]),
	) as Record<CollectorStatusFilter, number>
}

/** Rows the current tab admits. `all` admits everything. */
export function filterCollectorsByStatus(
	collectors: ReadonlyArray<CollectorSummary>,
	filter: CollectorStatusFilter,
): CollectorSummary[] {
	if (filter === 'all') return [...collectors]
	const status = FILTER_STATUS[filter]
	return collectors.filter((collector) => collector.status === status)
}
